import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { AuthContext } from '../../contexts/AuthContext'; // Import AuthContext
import HttpService from '../../service/HttpService'; // Import HttpService

const LoginModal = ({ onClose }) => {
    const { setAuthData } = useContext(AuthContext); // Access AuthContext
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false); // State for "Remember Me"
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false); // State to track loading
    const navigate = useNavigate(); // Initialize navigation
    const { login } = HttpService(); // Get the login function from HttpService

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true); // Start loading

        try {
            const data = await login(email, password); // Perform the login request
            setAuthData(data); // Store response in AuthContext

            // Persist authData to localStorage if "Remember Me" is checked
            if (rememberMe) {
                localStorage.setItem('authData', JSON.stringify(data));
            } else {
                localStorage.removeItem('authData'); // Ensure it's cleared if not remembered
            }

            onClose(); // Close the modal
            navigate('/dashboard'); // Redirect to the dashboard
        } catch (err) {
            if (err.message === 'timeout') {
                setError('Server unavailable, please try again.'); // Timeout-specific error message
            } else if (err.message === 'invalid_credentials') {
                setError('Login failed. Please check your credentials.'); // Invalid credentials error
            } else {
                setError('An unexpected error occurred. Please try again later.'); // General error message
            }
        } finally {
            setLoading(false); // Stop loading
        }
    };

    return (
        <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            {loading && (
                <div class="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div class="loader border-t-4 border-blue-500 rounded-full w-12 h-12 animate-spin"></div>
                </div>
            )}
            <div class={`bg-white rounded-xl p-8 w-full max-w-md relative ${loading ? 'opacity-50' : ''}`}>
                <button
                    class="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                    onClick={onClose}
                    disabled={loading} // Disable the close button while loading
                >
                    <i class="fas fa-times"></i>
                </button>
                <h2 class="text-2xl font-bold mb-6 text-gray-800">Login to Dashboard</h2>
                {error && <p class="text-red-500 mb-4">{error}</p>}
                <form onSubmit={handleLogin} autoComplete="on">
                    <div class="mb-6">
                        <label htmlFor="loginEmail" class="block text-gray-700 font-medium mb-2">Email</label>
                        <input
                            type="email"
                            id="loginEmail"
                            name="email"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            disabled={loading} // Disable input while loading
                        />
                    </div>
                    <div class="mb-6">
                        <label htmlFor="loginPassword" class="block text-gray-700 font-medium mb-2">Password</label>
                        <input
                            type="password"
                            id="loginPassword"
                            name="password"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            disabled={loading} // Disable input while loading
                        />
                    </div>
                    <div class="mb-6 flex items-center">
                        <input
                            type="checkbox"
                            id="rememberMe"
                            class="mr-2"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            disabled={loading} // Disable checkbox while loading
                        />
                        <label htmlFor="rememberMe" class="text-gray-700 font-medium">Remember Me</label>
                    </div>
                    <button
                        type="submit"
                        class="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition w-full mb-4"
                        disabled={loading} // Disable button while loading
                    >
                        {loading ? 'Logging in...' : 'Login'}
                    </button>
                    <div class="text-center">
                        <a href="#" class="text-blue-600 hover:underline">Forgot password?</a>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LoginModal;